import { Box } from '@chakra-ui/react'
import React, { useEffect, useRef } from 'react'
import { Layer } from 'styles/variables'

import { AppUpdatePrompt } from './AppUpdatePrompt'
import { InstallPrompt } from './InstallPrompt'
import { OfflineNotice } from './OfflineNotice'

export const PWA_PROMPTS_HEIGHT_VAR = '--wanda-pwa-prompts-height'

/**
 * The progressive-web-app banners share one slot pinned to the bottom of the screen.
 * They stack most urgent first, and the shared container owns both the positioning and
 * the home-indicator inset so the banners themselves stay plain blocks.
 *
 * Because the slot is fixed it would cover whatever sits at the bottom of the page, so
 * it publishes its height (0px while no banner shows) for the layout to make room for.
 */
export const PwaPrompts = () => {
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const element = ref.current
    if (!element || typeof ResizeObserver === 'undefined') {
      return
    }
    const root = document.documentElement
    const publishHeight = () => {
      // The empty slot still has its safe-area padding; only count it under a banner
      const height = element.childElementCount > 0 ? element.offsetHeight : 0
      root.style.setProperty(PWA_PROMPTS_HEIGHT_VAR, `${height}px`)
    }
    const observer = new ResizeObserver(publishHeight)
    observer.observe(element)
    publishHeight()
    return () => {
      observer.disconnect()
      root.style.removeProperty(PWA_PROMPTS_HEIGHT_VAR)
    }
  }, [])

  return (
    <Box
      ref={ref}
      position="fixed"
      bottom={0}
      left={0}
      right={0}
      zIndex={Layer.Overlay}
      pb="var(--wanda-safe-area-bottom)"
      pointerEvents="none"
      sx={{ '& > *': { pointerEvents: 'auto' } }}
    >
      <OfflineNotice />
      <AppUpdatePrompt />
      <InstallPrompt />
    </Box>
  )
}
